import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "es" | "en" | "fr";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  es: {
    // Navbar
    "nav.home": "Inicio",
    "nav.about": "Nosotros",
    "nav.destinations": "Destinos",
    "nav.tours": "Tours",
    "nav.blog": "Blog",
    "nav.contact": "Contacto",
    
    // Hero
    "hero.title": "Shikwaru Adventures",
    "hero.subtitle": "Descubre tu próxima gran aventura en África del sur",
    "hero.exploreTours": "Explorar Tours",
    "hero.contactUs": "Contáctanos",
    
    // Welcome
    "welcome.title": "¿Por qué viajar con Shikwaru Adventures?",
    "welcome.feature1": "Tours en Español, Francés e Inglés",
    "welcome.feature2": "Camping, Rango Medio y Lujo",
    "welcome.feature3": "Itinerarios Personalizados y Privados",
    "welcome.feature4": "14 Años de Servicio",
    
    // Tours Home
    "tours.title": "Tours Populares",
    "tours.subtitle": "Explora nuestras experiencias de safari más populares. También nos especializamos en tours a medida personalizados según tus preferencias.",
    "tours.days": "días",
    "tours.perPerson": "por persona",
    "tours.viewDetails": "Ver Detalles",
    "tours.viewAll": "Ver Todos los Tours",
    
    // Tours Page
    "toursPage.title": "Nuestros Safaris",
    "toursPage.subtitle": "Descubre nuestros tours más populares, o déjanos crear un itinerario a medida solo para ti",
    "toursPage.requestCustom": "Solicitar Tour Personalizado",
    "toursPage.mostPopular": "Más Popular",
    "toursPage.whatsIncluded": "Qué Incluye",
    "toursPage.notIncluded": "No Incluye",
    "toursPage.from": "Desde",
    "toursPage.inquireNow": "Consultar Ahora",
    "toursPage.lookingDifferent": "¿Buscas Algo Diferente?",
    "toursPage.tailorMade": "La mayoría de nuestros tours son a medida para coincidir con tus intereses específicos, presupuesto y fechas de viaje",
    "toursPage.createCustom": "Crea Tu Tour Personalizado",
    
    // Destinations Page
    "destinations.title": "Nuestros Destinos",
    "destinations.subtitle": "Explora los increíbles países donde operamos nuestros safaris",
    "destinations.bestTime": "Mejor Época para Visitar",
    "destinations.highlights": "Destacados",
    
    // About Page
    "about.title": "Sobre Shikwaru Adventures",
    "about.subtitle": "Creando experiencias de safari africanas inolvidables por más de 14 años",
    "about.ourStory": "Nuestra Historia",
    "about.story1": "Shikwaru Adventures es un operador turístico sudafricano dirigido por sus propietarios y con sede en Johannesburgo. Fundada por un grupo de guías profesionales, apasionados y altamente experimentados, la compañía se construyó sobre un profundo amor por la naturaleza, los viajes y las experiencias africanas auténticas. Con años de experiencia explorando y organizando viajes por el sur de África, entendemos lo que realmente define la calidad y el servicio excepcional. Nuestra misión es asegurar que cada huésped reciba no solo una atención y cuidado sobresalientes, sino también una perspectiva genuina, comodidad y una experiencia de viaje memorable de todo nuestro equipo.",
    "about.story2": "Nuestros guías son el corazón de Shikwaru Adventures y nuestro mayor activo. Son profesionales excepcionalmente conocedores, dedicados y responsables que poseen todas las cualificaciones necesarias para guiarte de manera segura y confiada por todo el sur de África. Más allá de su experiencia, son amigables, divertidos y están ansiosos por compartir su pasión por la naturaleza. La mayoría de ellos son multilingües, lo que nos permite ofrecer tours en varios idiomas y crear experiencias verdaderamente personalizadas para viajeros de todo el mundo.",
    "about.story3": "En Shikwaru Adventures, creemos en operar de manera responsable y sostenible. Apoyamos con orgullo a las comunidades locales, trabajamos con guías y proveedores de servicios locales, y contribuimos activamente a iniciativas de conservación. Nuestro equipo tiene un profundo respeto por el medio ambiente, y nos aseguramos de que cada producto y servicio que ofrecemos cumpla con nuestros altos estándares de comodidad, seguridad y sostenibilidad. Con Shikwaru Adventures, no solo viajas por África, la experimentas auténticamente, responsablemente y con personas que realmente se preocupan.",
    "about.whyChoose": "Por Qué Elegirnos",
    "about.expertGuides": "Guías Expertos",
    "about.expertGuidesDesc": "Guías profesionales multilingües con años de experiencia y profundo conocimiento de la vida silvestre y cultura africanas",
    "about.experience": "14+ Años de Experiencia",
    "about.experienceDesc": "Más de una década creando safaris perfectos, con asociaciones en todo el sur de África",
    "about.tailorMade": "Tours a Medida",
    "about.tailorMadeDesc": "Cada viajero es único. Personalizamos itinerarios para coincidir con tus intereses, presupuesto y estilo de viaje",
    "about.sustainable": "Turismo Sostenible",
    "about.sustainableDesc": "Estamos comprometidos con la conservación y el apoyo a las comunidades locales a través de prácticas de turismo responsable",
    "about.whatWeOffer": "Lo Que Ofrecemos",
    "about.campingTours": "Tours de Camping",
    "about.campingDesc": "Experiencias inmersivas bajo las estrellas africanas, perfectas para espíritus aventureros",
    "about.midrangeTours": "Tours de Rango Medio",
    "about.midrangeDesc": "Alojamientos cómodos con excelentes comodidades, equilibrando aventura y confort",
    "about.luxurySafaris": "Safaris de Lujo",
    "about.luxuryDesc": "Alojamientos premium y experiencias exclusivas para viajeros exigentes",
    
    // Contact Page
    "contact.title": "Contáctanos",
    "contact.subtitle": "¿Listo para comenzar tu aventura africana? Ponte en contacto con nosotros hoy",
    "contact.location": "Ubicación",
    "contact.phone": "Teléfono",
    "contact.email": "Correo",
    "contact.officeHours": "Horario de Oficina",
    "contact.mondayFriday": "Lunes - Viernes: 8:00 AM - 5:00 PM (CAT)",
    "contact.saturday": "Sábado: 9:00 AM - 1:00 PM",
    "contact.sunday": "Domingo: Cerrado",
    "contact.sendMessage": "Envíanos un Mensaje",
    "contact.formDesc": "Completa el formulario a continuación y nos pondremos en contacto contigo lo antes posible",
    "contact.fullName": "Nombre Completo",
    "contact.emailAddress": "Correo Electrónico",
    "contact.phoneNumber": "Número de Teléfono",
    "contact.message": "Mensaje",
    "contact.messagePlaceholder": "Cuéntanos sobre el safari de tus sueños...",
    "contact.sending": "Enviando...",
    "contact.sendButton": "Enviar Mensaje",
    "contact.messageSent": "¡Mensaje Enviado!",
    "contact.messageDesc": "Te responderemos en 24 horas.",
    "contact.tourInquiry": "Consulta sobre el tour",
    "contact.error": "Error",
    "contact.captchaError": "Por favor completa el captcha",
    "contact.captchaFailed": "Verificación de captcha fallida. Inténtalo de nuevo.",
    "contact.submitError": "Error al enviar el formulario. Inténtalo de nuevo.",
    "contact.emailError": "Error al enviar el correo electrónico. Inténtalo de nuevo.",
    
    // Reviews
    "reviews.title": "Lo Que Dicen Nuestros Clientes",
    "reviews.subtitle": "Descubre lo que nuestros huéspedes opinan sobre sus experiencias con Shikwaru Adventures. Consulta nuestras reseñas en SafariBookings o Google. ¡Si has viajado con nosotros, nos encantaría conocer tu opinión!",
    
    // Common
    "common.loading": "Cargando...",
    "common.max": "Máx",
    "common.people": "personas",
    
    // Partners
    "partners.title": "Nuestros Partners de Confianza",
    "partners.subtitle": "Trabajamos con las principales organizaciones turísticas de África para garantizar la mejor experiencia",
    
    // Itinerary
    "itinerary.view": "Ver Itinerario",
    "itinerary.title": "Itinerario",
    "itinerary.day": "Día",
    "itinerary.adventure": "de aventura",
  },
  en: {
    // Navbar
    
    "nav.home": "Home",
    "nav.about": "About Us",
    "nav.destinations": "Destinations",
    "nav.tours": "Tours",
    "nav.blog": "Blog",
    "nav.contact": "Contact",
    "hero.title": "Shikwaru Adventures",
    "hero.subtitle": "Discover your next great adventure in Southern Africa",
    "hero.exploreTours": "Explore Tours",
    "hero.contactUs": "Contact Us",
    "welcome.title": "Why travel with Shikwaru Adventures?",
    "welcome.feature1": "Spanish, French & English Tours",
    "welcome.feature2": "Camping, Midrange & Luxury",
    "welcome.feature3": "Custom & Private Itineraries",
    "welcome.feature4": "14 Years in the Service",
    "tours.title": "Popular Tours",
    "tours.subtitle": "Explore our most loved safari experiences. We also specialize in tailor-made tours customized to your preferences.",
    "tours.days": "days",
    "tours.perPerson": "per person",
    "tours.viewDetails": "View Details",
    "tours.viewAll": "View All Tours",
    "toursPage.title": "Our Safari Tours",
    "toursPage.subtitle": "Discover our most popular tours, or let us create a tailor-made itinerary just for you",
    "toursPage.requestCustom": "Request Custom Tour",
    "toursPage.mostPopular": "Most Popular",
    "toursPage.whatsIncluded": "What's Included",
    "toursPage.notIncluded": "Not Included",
    "toursPage.from": "From",
    "toursPage.inquireNow": "Inquire Now",
    "toursPage.lookingDifferent": "Looking for Something Different?",
    "toursPage.tailorMade": "Most of our tours are tailor-made to match your specific interests, budget, and travel dates",
    "toursPage.createCustom": "Create Your Custom Tour",
    "destinations.title": "Our Destinations",
    "destinations.subtitle": "Explore the incredible countries where we operate our safari tours",
    "destinations.bestTime": "Best Time to Visit",
    "destinations.highlights": "Highlights",
    "about.title": "About Shikwaru Adventures",
    "about.subtitle": "Creating unforgettable African safari experiences for over 14 years",
    "about.ourStory": "Our Story",
    "about.story1": "Shikwaru Adventures is an owner-run South African tour operator based in Johannesburg. Founded by a group of professional, passionate, and highly experienced guides, the company was built on a deep love for nature, travel, and authentic African experiences. With years of expertise in exploring and organizing journeys across Southern Africa, we understand what truly defines quality and exceptional service. Our mission is to ensure that every guest receives not only outstanding attention and care, but also genuine insight, comfort, and a memorable travel experience from our entire team.",
    "about.story2": "Our guides are the heart of Shikwaru Adventures and our greatest asset. They are exceptionally knowledgeable, dedicated, and responsible professionals who hold all the necessary qualifications to guide you safely and confidently throughout Southern Africa. Beyond their expertise, they are friendly, fun, and eager to share their passion for the wilderness. Most of them are multilingual, allowing us to offer tours in various languages and create truly personalized experiences for travelers from all over the world.",
    "about.story3": "At Shikwaru Adventures, we believe in operating responsibly and sustainably. We proudly support local communities, work with local guides and service providers, and actively contribute to conservation initiatives. Our team has a deep respect for the environment, and we ensure that every product and service we offer meets our high standards of comfort, safety, and sustainability. With Shikwaru Adventures, you don't just travel through Africa, you experience it authentically, responsibly, and with people who truly care.",
    "about.whyChoose": "Why Choose Us",
    "about.expertGuides": "Expert Guides",
    "about.expertGuidesDesc": "Multilingual professional guides with years of experience and deep knowledge of African wildlife and culture",
    "about.experience": "14+ Years Experience",
    "about.experienceDesc": "Over a decade of creating perfect safaris, with partnerships across Southern Africa",
    "about.tailorMade": "Tailor-Made Tours",
    "about.tailorMadeDesc": "Every traveler is unique. We customize itineraries to match your interests, budget, and travel style",
    "about.sustainable": "Sustainable Tourism",
    "about.sustainableDesc": "We're committed to conservation and supporting local communities through responsible tourism practices",
    "about.whatWeOffer": "What We Offer",
    "about.campingTours": "Camping Tours",
    "about.campingDesc": "Immersive experiences under African stars, perfect for adventurous spirits",
    "about.midrangeTours": "Midrange Tours",
    "about.midrangeDesc": "Comfortable lodges with great amenities, balancing adventure and comfort",
    "about.luxurySafaris": "Luxury Safaris",
    "about.luxuryDesc": "Premium accommodations and exclusive experiences for discerning travelers",
    "contact.title": "Contact Us",
    "contact.subtitle": "Ready to start your African adventure? Get in touch with us today",
    "contact.location": "Location",
    "contact.phone": "Phone",
    "contact.email": "Email",
    "contact.officeHours": "Office Hours",
    "contact.mondayFriday": "Monday - Friday: 8:00 AM - 5:00 PM (CAT)",
    "contact.saturday": "Saturday: 9:00 AM - 1:00 PM",
    "contact.sunday": "Sunday: Closed",
    "contact.sendMessage": "Send us a Message",
    "contact.formDesc": "Fill out the form below and we'll get back to you as soon as possible",
    "contact.fullName": "Full Name",
    "contact.emailAddress": "Email Address",
    "contact.phoneNumber": "Phone Number",
    "contact.message": "Message",
    "contact.messagePlaceholder": "Tell us about your dream safari...",
    "contact.sending": "Sending...",
    "contact.sendButton": "Send Message",
    "contact.messageSent": "Message Sent!",
    "contact.messageDesc": "We'll get back to you within 24 hours.",
    "contact.tourInquiry": "Inquiry about tour",
    "contact.error": "Error",
    "contact.captchaError": "Please complete the captcha",
    "contact.captchaFailed": "Captcha verification failed. Please try again.",
    "contact.submitError": "Error submitting form. Please try again.",
    "contact.emailError": "Error sending email. Please try again.",
    "reviews.title": "What Our Clients Say",
    "reviews.subtitle": "Discover what our guests have to say about their Shikwaru Adventures experiences. Check out our reviews on SafariBookings or Google. If you've traveled with us, we'd love to hear your feedback!",
    "common.loading": "Loading...",
    "common.max": "Max",
    "common.people": "people",
    
    // Partners
    "partners.title": "Our Trusted Partners",
    "partners.subtitle": "We work with Africa's leading tourism organizations to ensure the best experience",
    
    // Itinerary
    "itinerary.view": "View Itinerary",
    "itinerary.title": "Itinerary",
    "itinerary.day": "Day",
    "itinerary.adventure": "of adventure",
  },
  fr: {
    // Navbar
    
    "nav.home": "Accueil",
    "nav.about": "À Propos",
    "nav.destinations": "Destinations",
    "nav.tours": "Circuits",
    "nav.blog": "Blog",
    "nav.contact": "Contact",
    "hero.title": "Shikwaru Adventures",
    "hero.subtitle": "Découvrez votre prochaine grande aventure au Afrique du Sud",
    "hero.exploreTours": "Explorer les Circuits",
    "hero.contactUs": "Nous Contacter",
    "welcome.title": "Pourquoi voyager avec Shikwaru Adventures?",
    "welcome.feature1": "Circuits en Espagnol, Français et Anglais",
    "welcome.feature2": "Camping, Gamme Moyenne et Luxe",
    "welcome.feature3": "Itinéraires Personnalisés et Privés",
    "welcome.feature4": "14 Ans de Service",
    "tours.title": "Circuits Populaires",
    "tours.subtitle": "Explorez nos expériences de safari les plus appréciées. Nous sommes également spécialisés dans les circuits sur mesure personnalisés selon vos préférences.",
    "tours.days": "jours",
    "tours.perPerson": "par personne",
    "tours.viewDetails": "Voir les Détails",
    "tours.viewAll": "Voir Tous les Circuits",
    "toursPage.title": "Nos Circuits Safari",
    "toursPage.subtitle": "Découvrez nos circuits les plus populaires, ou laissez-nous créer un itinéraire sur mesure rien que pour vous",
    "toursPage.requestCustom": "Demander un Circuit Personnalisé",
    "toursPage.mostPopular": "Plus Populaire",
    "toursPage.whatsIncluded": "Ce Qui Est Inclus",
    "toursPage.notIncluded": "Non Inclus",
    "toursPage.from": "À partir de",
    "toursPage.inquireNow": "Demander Maintenant",
    "toursPage.lookingDifferent": "Vous Cherchez Quelque Chose de Différent?",
    "toursPage.tailorMade": "La plupart de nos circuits sont sur mesure pour correspondre à vos intérêts spécifiques, budget et dates de voyage",
    "toursPage.createCustom": "Créez Votre Circuit Personnalisé",
    "destinations.title": "Nos Destinations",
    "destinations.subtitle": "Explorez les pays incroyables où nous opérons nos circuits safari",
    "destinations.bestTime": "Meilleure Période pour Visiter",
    "destinations.highlights": "Points Forts",
    "about.title": "À Propos de Shikwaru Adventures",
    "about.subtitle": "Créer des expériences de safari africaines inoubliables depuis plus de 14 ans",
    "about.ourStory": "Notre Histoire",
    "about.story1": "Shikwaru Adventures est un voyagiste sud-africain géré par ses propriétaires et basé à Johannesburg. Fondée par un groupe de guides professionnels, passionnés et hautement expérimentés, la société a été construite sur un amour profond de la nature, du voyage et des expériences africaines authentiques. Avec des années d'expertise dans l'exploration et l'organisation de voyages à travers l'Afrique australe, nous comprenons ce qui définit vraiment la qualité et le service exceptionnel. Notre mission est de garantir que chaque client reçoive non seulement une attention et des soins exceptionnels, mais aussi une véritable perspicacité, un confort et une expérience de voyage mémorable de toute notre équipe.",
    "about.story2": "Nos guides sont le cœur de Shikwaru Adventures et notre plus grand atout. Ce sont des professionnels exceptionnellement compétents, dévoués et responsables qui détiennent toutes les qualifications nécessaires pour vous guider en toute sécurité et avec confiance dans toute l'Afrique australe. Au-delà de leur expertise, ils sont amicaux, amusants et désireux de partager leur passion pour la nature sauvage. La plupart d'entre eux sont multilingues, ce qui nous permet d'offrir des circuits dans diverses langues et de créer des expériences véritablement personnalisées pour les voyageurs du monde entier.",
    "about.story3": "Chez Shikwaru Adventures, nous croyons en l'exploitation responsable et durable. Nous soutenons fièrement les communautés locales, travaillons avec des guides et des prestataires de services locaux, et contribuons activement aux initiatives de conservation. Notre équipe a un profond respect pour l'environnement, et nous veillons à ce que chaque produit et service que nous offrons réponde à nos normes élevées de confort, de sécurité et de durabilité. Avec Shikwaru Adventures, vous ne voyagez pas seulement à travers l'Afrique, vous la vivez de manière authentique, responsable et avec des personnes qui se soucient vraiment de vous.",
    "about.whyChoose": "Pourquoi Nous Choisir",
    "about.expertGuides": "Guides Experts",
    "about.expertGuidesDesc": "Guides professionnels multilingues avec des années d'expérience et une connaissance approfondie de la faune et de la culture africaines",
    "about.experience": "Plus de 14 Ans d'Expérience",
    "about.experienceDesc": "Plus d'une décennie à créer des safaris parfaits, avec des partenariats à travers l'Afrique australe",
    "about.tailorMade": "Circuits Sur Mesure",
    "about.tailorMadeDesc": "Chaque voyageur est unique. Nous personnalisons les itinéraires pour correspondre à vos intérêts, budget et style de voyage",
    "about.sustainable": "Tourisme Durable",
    "about.sustainableDesc": "Nous sommes engagés dans la conservation et le soutien des communautés locales grâce à des pratiques de tourisme responsable",
    "about.whatWeOffer": "Ce Que Nous Offrons",
    "about.campingTours": "Circuits Camping",
    "about.campingDesc": "Expériences immersives sous les étoiles africaines, parfaites pour les esprits aventureux",
    "about.midrangeTours": "Circuits Gamme Moyenne",
    "about.midrangeDesc": "Lodges confortables avec d'excellentes commodités, équilibrant aventure et confort",
    "about.luxurySafaris": "Safaris de Luxe",
    "about.luxuryDesc": "Hébergements premium et expériences exclusives pour les voyageurs exigeants",
    "contact.title": "Nous Contacter",
    "contact.subtitle": "Prêt à commencer votre aventure africaine? Contactez-nous aujourd'hui",
    "contact.location": "Localisation",
    "contact.phone": "Téléphone",
    "contact.email": "Email",
    "contact.officeHours": "Heures d'Ouverture",
    "contact.mondayFriday": "Lundi - Vendredi: 8h00 - 17h00 (CAT)",
    "contact.saturday": "Samedi: 9h00 - 13h00",
    "contact.sunday": "Dimanche: Fermé",
    "contact.sendMessage": "Envoyez-nous un Message",
    "contact.formDesc": "Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais",
    "contact.fullName": "Nom Complet",
    "contact.emailAddress": "Adresse Email",
    "contact.phoneNumber": "Numéro de Téléphone",
    "contact.message": "Message",
    "contact.messagePlaceholder": "Parlez-nous de votre safari de rêve...",
    "contact.sending": "Envoi...",
    "contact.sendButton": "Envoyer le Message",
    "contact.messageSent": "Message Envoyé!",
    "contact.messageDesc": "Nous vous répondrons dans les 24 heures.",
    "contact.tourInquiry": "Demande concernant le circuit",
    "contact.error": "Erreur",
    "contact.captchaError": "Veuillez compléter le captcha",
    "contact.captchaFailed": "Échec de la vérification du captcha. Réessayez.",
    "contact.submitError": "Erreur lors de l'envoi du formulaire. Réessayez.",
    "contact.emailError": "Erreur lors de l'envoi de l'e-mail. Réessayez.",
    "reviews.title": "Ce Que Disent Nos Clients",
    "reviews.subtitle": "Découvrez les avis de nos clients sur leurs expériences avec Shikwaru Adventures. Consultez nos avis sur SafariBookings ou Google. Si vous avez voyagé avec nous, nous serions ravis de connaître votre retour !",
    "common.loading": "Chargement...",
    "common.max": "Max",
    "common.people": "personnes",
    
    // Partners
    "partners.title": "Nos Partenaires de Confiance",
    "partners.subtitle": "Nous travaillons avec les principales organisations touristiques d'Afrique pour garantir la meilleure expérience",
    
    // Itinerary
    "itinerary.view": "Voir l'Itinéraire",
    "itinerary.title": "Itinéraire",
    "itinerary.day": "Jour",
    "itinerary.adventure": "d'aventure",
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    if (saved) {
      return saved as Language;
    }
    
    // Detect browser language
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('es')) return 'es';
    if (browserLang.startsWith('fr')) return 'fr';
    if (browserLang.startsWith('en')) return 'en';
    
    // Default fallback
    return "es";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
